import {Component, EventEmitter, forwardRef, Input, Output} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';

export interface Sale4AllInputFile {
  file?: File;
  link?: string;
  preview?: string | ArrayBuffer;
}

@Component({
    selector: 'input-file',
    templateUrl: './input-file.component.html',
    styleUrls: ['./input-file.component.scss'],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputFileComponent),
            multi: true
        }],
    standalone: false
})
export class InputFileComponent implements ControlValueAccessor {
  @Input() fileAccept = '*';
  @Input() fileLimit: number | string = 1;
  @Input() sizeLimit: number | string | null = null;
  @Input() placeholder = '';
  @Output() acceptedFile = new EventEmitter<Sale4AllInputFile>();
  @Output() deletedFile = new EventEmitter<Sale4AllInputFile>();
  @Output() rejectedFile = new EventEmitter<{reason: string; file: Sale4AllInputFile}>();

  files: Sale4AllInputFile[] = [];
  disabled = false;
  isDragging = false;
  private onChange: (files: Sale4AllInputFile[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  get limit(): number {
    const value = Number(this.fileLimit);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  get canAddFile(): boolean {
    return !this.disabled && this.files.length < this.limit;
  }

  writeValue(value: Sale4AllInputFile[] | null): void {
    this.files = Array.isArray(value) ? [...value] : [];
  }

  registerOnChange(fn: (files: Sale4AllInputFile[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  selectFiles(fileList: FileList | null): void {
    if (!fileList || this.disabled) return;
    const next = [...this.files];
    Array.from(fileList).forEach(file => {
      const inputFile: Sale4AllInputFile = {file};
      if (next.length >= this.limit) {
        this.rejectedFile.emit({reason: 'limitReached', file: inputFile});
      } else if (!this.isAccepted(file)) {
        this.rejectedFile.emit({reason: 'badFile', file: inputFile});
      } else if (!this.isWithinSizeLimit(file)) {
        this.rejectedFile.emit({reason: 'sizeReached', file: inputFile});
      } else {
        next.push(inputFile);
        this.createPreview(inputFile);
        this.acceptedFile.emit(inputFile);
      }
    });
    this.updateValue(next);
  }

  replaceFile(fileList: FileList | null, index: number): void {
    const file = fileList?.item(0);
    if (!file || this.disabled) return;
    const inputFile: Sale4AllInputFile = {file};
    if (!this.isAccepted(file) || !this.isWithinSizeLimit(file)) {
      this.rejectedFile.emit({reason: !this.isAccepted(file) ? 'badFile' : 'sizeReached', file: inputFile});
      return;
    }
    const next = [...this.files];
    next[index] = inputFile;
    this.createPreview(inputFile);
    this.acceptedFile.emit(inputFile);
    this.updateValue(next);
  }

  removeFile(index: number): void {
    if (this.disabled) return;
    const next = [...this.files];
    const [removed] = next.splice(index, 1);
    this.deletedFile.emit(removed);
    this.updateValue(next);
  }

  drop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    this.selectFiles(event.dataTransfer?.files || null);
  }

  dragOver(event: DragEvent): void {
    event.preventDefault();
    if (!this.disabled) this.isDragging = true;
  }

  private updateValue(files: Sale4AllInputFile[]): void {
    this.files = files;
    this.onChange(this.files);
    this.onTouched();
  }

  private createPreview(inputFile: Sale4AllInputFile): void {
    if (!inputFile.file?.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => inputFile.preview = reader.result;
    reader.readAsDataURL(inputFile.file);
  }

  private isWithinSizeLimit(file: File): boolean {
    const megabytes = Number(this.sizeLimit);
    return !Number.isFinite(megabytes) || megabytes <= 0 || file.size < megabytes * 1024 * 1024;
  }

  private isAccepted(file: File): boolean {
    if (!this.fileAccept || this.fileAccept === '*') return true;
    return this.fileAccept.split(',').some(rawType => {
      const type = rawType.trim();
      if (type.startsWith('.')) return file.name.toLowerCase().endsWith(type.toLowerCase());
      if (type.endsWith('/*')) return file.type.startsWith(type.slice(0, -1));
      return file.type === type;
    });
  }
}
